import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Tv, Film, Clapperboard, Radio, Star, Clock, Search,
  Play, Pause, Volume2, VolumeX, Maximize, Settings, Menu,
  List, Loader2, ChevronRight, ArrowLeft, X, Info, Calendar, Timer, Tag, Users
} from "lucide-react";
import axios from "axios";
import Hls from "hls.js";
import mpegts from "mpegts.js";
import { brand, brandId } from "../brand.config";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONTENT_TABS = [
  { id: "live", label: "LIVE TV", icon: Tv },
  { id: "movies", label: "MOVIES", icon: Film },
  { id: "series", label: "SERIES", icon: Clapperboard },
  { id: "radio", label: "RADIO", icon: Radio },
];

const PlayerPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const mpegtsRef = useRef(null);
  // FIX #4/#7: track in-flight resume interval so we cancel on channel switch
  const resumeIntervalRef = useRef(null);
  // FIX #6: track EPG prefetch timeouts so we cancel on dep change
  const epgPrefetchTimersRef = useRef([]);
  // FIX #9: throttle localStorage progress writes
  const lastProgressSaveRef = useRef(0);
  // FIX #7: always point to the latest restoreProgress (avoid stale closure)
  const restoreProgressRef = useRef(null);

  const [deviceKey, setDeviceKey] = useState("");
  const [deviceStatus, setDeviceStatus] = useState(null);
  const [playlists, setPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [categories, setCategories] = useState([]);
  const [channels, setChannels] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [contentType, setContentType] = useState(searchParams.get("type") || "live");
  const [loading, setLoading] = useState(true);
  const [loadingChannels, setLoadingChannels] = useState(false);

  // Player states
  const [isPlaying, setIsPlaying] = useState(false);
  // FIX #3: start muted so autoPlay isn't blocked; user unmutes via custom button
  const [isMuted, setIsMuted] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentTime, setCurrentTime] = useState(new Date());
  const [streamError, setStreamError] = useState(null);
  // FIX #18: remember the upstream URL + container when a format error occurs,
  // so we can offer "Open in VLC / MX Player" via Android intent
  const [unplayableStream, setUnplayableStream] = useState(null);

  // Favorites & Recent
  const [favorites, setFavorites] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);

  // Series state
  const [seriesInfo, setSeriesInfo] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [showSeriesModal, setShowSeriesModal] = useState(false);

  // Movie info state
  const [movieInfo, setMovieInfo] = useState(null);
  const [showMovieModal, setShowMovieModal] = useState(false);
  const [loadingInfo, setLoadingInfo] = useState(false);

  // EPG state
  const [epgData, setEpgData] = useState([]);
  const [epgCache, setEpgCache] = useState({});

  // Continue watching
  const [watchProgress, setWatchProgress] = useState({});

  // Mobile panel view
  const [mobilePanel, setMobilePanel] = useState("channels"); // "categories", "channels", "player"

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const storedFavorites = localStorage.getItem("nettv_favorites");
    const storedRecent = localStorage.getItem("nettv_recent");
    const storedProgress = localStorage.getItem("nettv_progress");
    if (storedFavorites) setFavorites(JSON.parse(storedFavorites));
    if (storedRecent) setRecentlyViewed(JSON.parse(storedRecent));
    if (storedProgress) setWatchProgress(JSON.parse(storedProgress));
  }, []);

  useEffect(() => {
    const checkDevice = async () => {
      const storedKey = localStorage.getItem("deviceKey");
      if (!storedKey) { navigate("/activate"); return; }
      try {
        const response = await axios.get(`${API}/device/status/${storedKey}`);
        setDeviceKey(storedKey);
        setDeviceStatus(response.data);
        if (response.data.status === "expired" || response.data.status === "pending") {
          navigate("/activate"); return;
        }
        await fetchPlaylists(storedKey);
      } catch {
        navigate("/activate");
      } finally {
        setLoading(false);
      }
    };
    checkDevice();
    return () => {
      if (hlsRef.current) hlsRef.current.destroy();
      if (mpegtsRef.current) mpegtsRef.current.destroy();
    };
  }, [navigate]);

  const fetchPlaylists = async (key) => {
    try {
      const response = await axios.get(`${API}/playlist/${key}`);
      setPlaylists(response.data);
      const storedPlaylistId = localStorage.getItem("selectedPlaylistId");
      const playlist = response.data.find((p) => p.id === storedPlaylistId) || response.data[0];
      if (playlist) {
        setSelectedPlaylist(playlist);
        await fetchContent(playlist, contentType, key);
      }
    } catch (error) {
      if (error.response?.status === 403) { toast.error("Access expired"); navigate("/activate"); }
      else toast.error("Failed to load playlists");
    }
  };

  const fetchContent = async (playlist, type, key = null) => {
    if (!playlist) return;
    const dk = key || deviceKey;
    setLoadingChannels(true);
    setContentType(type);
    setSelectedChannel(null);
    setSeriesInfo(null);
    setShowSeriesModal(false);

    try {
      if (playlist.playlist_type === "xtream") {
        let action, catAction;
        switch (type) {
          case "movies":
            action = "get_vod_streams"; catAction = "get_vod_categories"; break;
          case "series":
            action = "get_series"; catAction = "get_series_categories"; break;
          default:
            action = "get_live_streams"; catAction = "get_live_categories"; break;
        }

        const [catRes, streamRes] = await Promise.all([
          axios.get(`${API}/xtream/player_api`, { params: { device_key: dk, playlist_id: playlist.id, action: catAction } }),
          axios.get(`${API}/xtream/player_api`, { params: { device_key: dk, playlist_id: playlist.id, action } }),
        ]);
        setCategories(catRes.data || []);
        setChannels(streamRes.data || []);
        setSelectedCategory("all");
      } else if (playlist.playlist_type === "m3u" && playlist.m3u_url) {
        const m3uType = type === "movies" ? "vod" : type === "series" ? "series" : type === "radio" ? "radio" : "live";
        const response = await axios.get(`${API}/m3u/channels`, {
          params: { device_key: dk, playlist_id: playlist.id, content_type: m3uType },
        });
        setChannels(response.data.channels || []);
        setCategories(response.data.categories || []);
        setSelectedCategory("all");
        toast.success(`Loaded ${response.data.total} items`);
      }
    } catch {
      toast.error("Failed to load content");
    } finally {
      setLoadingChannels(false);
    }
  };

  const destroyPlayers = useCallback(() => {
    try {
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
    } catch (e) { hlsRef.current = null; }
    try {
      if (mpegtsRef.current) { mpegtsRef.current.destroy(); mpegtsRef.current = null; }
    } catch (e) { mpegtsRef.current = null; }
    try {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.removeAttribute('src');
        // FIX #11: call .load() to actually abort the previous request
        try { videoRef.current.load(); } catch (e) { /* ignore */ }
      }
    } catch (e) { /* ignore */ }
    // FIX #4: cancel any in-flight resume polling from a previous channel
    if (resumeIntervalRef.current) {
      clearInterval(resumeIntervalRef.current);
      resumeIntervalRef.current = null;
    }
  }, []);

  const playStream = useCallback(async (streamUrl, useProxy = false) => {
    if (!streamUrl || !videoRef.current) return;
    setStreamError(null);
    setUnplayableStream(null); // FIX #18: clear external-player offer on every new stream
    destroyPlayers();

    // FIX (HMAC): /api/stream/proxy now requires a short-lived HMAC token.
    // Fetch the signed URL upfront so the rest of the function can stay
    // synchronous. If signing fails, fall back to direct playback.
    const signProxyUrl = async (rawUrl) => {
      try {
        const cleanUrl = rawUrl.startsWith("http%") ? decodeURIComponent(rawUrl) : rawUrl;
        const res = await axios.get(`${API}/stream/sign`, {
          params: { url: cleanUrl, device_key: deviceKey },
          timeout: 8000,
        });
        return res.data && res.data.signed_url ? res.data.signed_url : null;
      } catch (e) {
        return null;
      }
    };

    // Pre-sign the primary URL (only when proxying).
    let primarySigned = null;
    if (useProxy) {
      primarySigned = await signProxyUrl(streamUrl);
      if (!primarySigned) {
        setStreamError("Stream authorization failed. Please refresh.");
        return;
      }
    }
    // Convenience: returns signed URL for any URL, lazily signing alts.
    const proxyUrl = (url) => {
      if (!useProxy) return url;
      if (url === streamUrl && primarySigned) return primarySigned;
      // Async signing inside a sync call — use synchronous XHR via window.fetch
      // is not allowed. We deliberately keep this synchronous-shaped fallback
      // unsigned; callers that need an alt-signed URL must await signProxyUrl.
      return primarySigned || url;
    };

    // FIX #3: try to play; if blocked by autoplay policy, mute & retry once
    const playWithAutoplayFallback = (onSuccess, onFail) => {
      const v = videoRef.current;
      if (!v) return;
      const attempt = () => v.play().then(() => onSuccess && onSuccess()).catch((err) => {
        if (err && err.name === "NotAllowedError" && !v.muted) {
          // Autoplay-with-sound blocked — mute and retry
          try { v.muted = true; setIsMuted(true); } catch (e) {}
          v.play().then(() => onSuccess && onSuccess()).catch(() => onFail && onFail(err));
        } else {
          onFail && onFail(err);
        }
      });
      attempt();
    };

    const urlLower = streamUrl.toLowerCase();
    const isHLS = urlLower.includes(".m3u8");
    const isFLV = urlLower.includes(".flv");
    const isTS = urlLower.includes(".ts");
    const isNativeVideo = /\.(mp4|mkv|avi|mov|webm)/.test(urlLower);

    // For movies/series: use native HTML5 video unless it's clearly a TS/HLS/FLV stream
    // Xtream-to-M3U URLs often lack file extensions (e.g., /movie/user/pass/12345)
    // mpegts.js can ONLY handle MPEG-TS and FLV — it crashes on MP4/MKV containers
    const isVodContent = contentType === "movies" || contentType === "series";
    const useNativePlayer = isNativeVideo || (isVodContent && !isHLS && !isFLV && !isTS);

    if (useNativePlayer) {
      const src = useProxy ? proxyUrl(streamUrl) : streamUrl;
      videoRef.current.src = src;

      // FIX #18: if MKV/AVI fails (Android WebView can't decode), retry with .mp4 swap.
      // Many Xtream servers remux/transcode on-the-fly when the requested extension changes.
      // If THAT also fails, surface external-player ("Open in VLC / MX Player") buttons.
      const isMkvOrAvi = /\.(mkv|avi)(\?|$)/i.test(streamUrl);
      const onNativeFail = async () => {
        if (isMkvOrAvi) {
          const altUrl = streamUrl.replace(/\.(mkv|avi)(\?|$)/i, ".mp4$2");
          const altSigned = useProxy ? await signProxyUrl(altUrl) : altUrl;
          const altSrc = altSigned || altUrl;
          videoRef.current.src = altSrc;
          playWithAutoplayFallback(
            () => setIsPlaying(true),
            () => {
              // Both attempts failed → offer external player
              setStreamError("This format requires an external player");
              setUnplayableStream({
                url: streamUrl,
                proxyUrl: useProxy ? proxyUrl(streamUrl) : streamUrl,
                name: (selectedChannel && selectedChannel.name) || "stream",
              });
            }
          );
          return;
        }
        // Non-MKV/AVI native failure — surface the same external-player banner
        setStreamError("Unable to play this video format");
        setUnplayableStream({
          url: streamUrl,
          proxyUrl: useProxy ? proxyUrl(streamUrl) : streamUrl,
          name: (selectedChannel && selectedChannel.name) || "stream",
        });
      };
      playWithAutoplayFallback(() => setIsPlaying(true), onNativeFail);
      return;
    }

    const tryMpegTs = (url) => {
      if (!mpegts.isSupported()) {
        videoRef.current.src = useProxy ? proxyUrl(url) : url;
        playWithAutoplayFallback(() => setIsPlaying(true), () => setStreamError("Unable to play this stream format"));
        return;
      }
      try {
        const player = mpegts.createPlayer({
          type: "mpegts",
          isLive: contentType === "live" || contentType === "radio",
          url: useProxy ? proxyUrl(url) : url,
          cors: true,
        }, {
          // FIX #10: offload TS demuxing to worker thread — huge win for 50+ Mbps streams on mobile
          enableWorker: true,
          enableStashBuffer: true,
          stashInitialSize: 128,
          autoCleanupSourceBuffer: true,
          fixAudioTimestampGap: true,
        });
        mpegtsRef.current = player;
        player.attachMediaElement(videoRef.current);
        player.load();
        playWithAutoplayFallback(() => setIsPlaying(true));
        player.on(mpegts.Events.ERROR, () => {
          setStreamError("Stream error. Try another channel.");
        });
      } catch {
        videoRef.current.src = useProxy ? proxyUrl(url) : url;
        playWithAutoplayFallback(() => setIsPlaying(true), () => setStreamError("Unable to play this stream format"));
      }
    };

    if (isHLS) {
      if (Hls.isSupported()) {
        // Extract stream origin for URL correction
        let streamOrigin = '';
        try { streamOrigin = new URL(streamUrl).origin; } catch(e) {}
        const appOrigin = window.location.origin;

        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
          // NOTE: child segment URLs are signed by the backend's HLS
          // manifest rewriter (`/api/stream/hls-proxy`). xhrSetup is left
          // unset so Hls.js fetches them as-is. If Hls.js wrongly resolves
          // a relative URL against the app origin we'll see a 404, but the
          // backend rewriter absolutizes everything so this is rare.
        });
        hlsRef.current = hls;
        // When proxying, use the pre-signed /api/stream/hls-proxy URL so
        // the manifest itself is fetched through our backend (which then
        // rewrites every child URL with its own fresh signature).
        hls.loadSource(useProxy ? proxyUrl(streamUrl) : streamUrl);
        hls.attachMedia(videoRef.current);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          playWithAutoplayFallback(() => setIsPlaying(true));
        });
        hls.on(Hls.Events.ERROR, (_, data) => {
          if (data.fatal) {
            try { hls.destroy(); } catch(e) {}
            hlsRef.current = null;
            setStreamError("Stream unavailable. Try another channel.");
          }
        });
      } else if (videoRef.current.canPlayType("application/vnd.apple.mpegurl")) {
        // Safari native HLS
        videoRef.current.src = useProxy ? proxyUrl(streamUrl) : streamUrl;
        playWithAutoplayFallback(() => setIsPlaying(true));
      }
    } else if (isFLV) {
      if (mpegts.isSupported()) {
        try {
          const player = mpegts.createPlayer({
            type: "flv", isLive: true,
            url: useProxy ? proxyUrl(streamUrl) : streamUrl, cors: true,
          });
          mpegtsRef.current = player;
          player.attachMediaElement(videoRef.current);
          player.load();
          playWithAutoplayFallback(() => setIsPlaying(true));
        } catch {
          setStreamError("Unable to play FLV stream");
        }
      }
    } else {
      // Raw TS or unknown format
      tryMpegTs(streamUrl);
    }
  }, [deviceKey, contentType, destroyPlayers]);

  const addToRecent = useCallback((channel) => {
    const newRecent = [channel, ...recentlyViewed.filter((c) => c.stream_id !== channel.stream_id)].slice(0, 20);
    setRecentlyViewed(newRecent);
    localStorage.setItem("nettv_recent", JSON.stringify(newRecent));
  }, [recentlyViewed]);

  const startPlayback = useCallback((channel) => {
    if (!selectedPlaylist || !channel) return;
    setSelectedChannel(channel);
    addToRecent(channel);
    if (contentType === "movies" || contentType === "series") {
      // FIX #7: call latest restoreProgress via ref (avoids stale closure)
      setTimeout(() => {
        if (restoreProgressRef.current) restoreProgressRef.current(channel);
      }, 2000);
    }

    let streamUrl = "";
    let useProxy = false;

    if (selectedPlaylist.playlist_type === "xtream") {
      const baseUrl = selectedPlaylist.xtream_url.replace(/\/$/, "");
      const username = selectedPlaylist.xtream_username;
      const password = selectedPlaylist.xtream_password;

      if (contentType === "live" || contentType === "radio") {
        // Use the raw stream-id path (no extension) — this is MPEG-TS and
        // works reliably through our proxy on every device. The `.m3u8`
        // variant returns a 404 HTML page on many Xtream servers (observed
        // on 2.59.169.70:25461) and direct upstream HTTP fails on Android
        // WebView due to mixed-content blocking inside our HTTPS shell.
        streamUrl = `${baseUrl}/${username}/${password}/${channel.stream_id}`;
        useProxy = true;
      } else if (contentType === "movies") {
        streamUrl = `${baseUrl}/movie/${username}/${password}/${channel.stream_id}.ts`;
        useProxy = true;
      } else if (contentType === "series") {
        streamUrl = `${baseUrl}/series/${username}/${password}/${channel.stream_id}.ts`;
        useProxy = true;
      }
    } else if (selectedPlaylist.playlist_type === "m3u") {
      const rawUrl = channel.stream_url || "";

      // For M3U playlists: use the raw stream URL directly through our proxy
      // Note: Converting to .m3u8 doesn't work because IPTV servers IP-lock HLS segments
      // Raw TS streams work reliably through the proxy
      streamUrl = rawUrl;
      useProxy = true;
    }

    if (streamUrl) playStream(streamUrl, useProxy);

    // Watch-log telemetry (non-blocking, best-effort)
    if (deviceKey && channel.name) {
      axios.post(`${API}/watch-log`, {
        device_key: deviceKey,
        channel_name: channel.name || "",
        category: channel.category_name || contentType,
        epg_code: channel.epg_channel_id || "",
      }).catch(() => {});
    }

    // Fetch EPG for live channels
    if (contentType === "live" && selectedPlaylist && channel.stream_id) {
      fetchEPG(channel.stream_id, channel.name);
    }
  }, [selectedPlaylist, contentType, addToRecent, playStream, deviceKey]);

  const handleMovieClick = useCallback(async (movie) => {
    setSelectedChannel(movie);
    setLoadingInfo(true);

    // For Xtream playlists, fetch detailed movie info
    if (selectedPlaylist?.playlist_type === "xtream" && movie.stream_id) {
      try {
        const res = await axios.get(`${API}/xtream/vod_info`, {
          params: { device_key: deviceKey, playlist_id: selectedPlaylist.id, vod_id: movie.stream_id },
        });
        setMovieInfo({ ...movie, ...(res.data?.info || {}), vodInfo: res.data });
      } catch {
        // Fallback to basic info from the channel list
        setMovieInfo(movie);
      }
    } else {
      // M3U - show basic info
      setMovieInfo(movie);
    }

    setLoadingInfo(false);
    setShowMovieModal(true);
  }, [selectedPlaylist, deviceKey]);

  const playMovieFromModal = useCallback(() => {
    if (!movieInfo) return;
    setShowMovieModal(false);
    startPlayback(movieInfo);
  }, [movieInfo, startPlayback]);

  const playChannel = useCallback((channel) => {
    if (!selectedPlaylist || !channel) return;

    // For movies, show info modal first
    if (contentType === "movies") {
      handleMovieClick(channel);
      return;
    }

    startPlayback(channel);
  }, [selectedPlaylist, contentType, startPlayback, handleMovieClick]);

  const handleSeriesClick = useCallback(async (series) => {
    setSelectedChannel(series);
    setLoadingInfo(true);

    if (selectedPlaylist?.playlist_type === "xtream" && series.series_id) {
      try {
        const res = await axios.get(`${API}/xtream/series_info`, {
          params: { device_key: deviceKey, playlist_id: selectedPlaylist.id, series_id: series.series_id },
        });
        setSeriesInfo({ ...res.data, _channel: series });
        const seasons = res.data?.episodes ? Object.keys(res.data.episodes) : [];
        if (seasons.length > 0) setSelectedSeason(seasons[0]);
      } catch {
        setSeriesInfo({ _channel: series, info: {}, episodes: {} });
      }
    } else {
      // M3U series - basic info
      setSeriesInfo({ _channel: series, info: { name: series.name }, episodes: {} });
    }

    setLoadingInfo(false);
    setShowSeriesModal(true);
  }, [selectedPlaylist, deviceKey]);

  const playEpisode = useCallback((episode) => {
    if (!selectedPlaylist) return;
    const baseUrl = selectedPlaylist.xtream_url.replace(/\/$/, "");
    const username = selectedPlaylist.xtream_username;
    const password = selectedPlaylist.xtream_password;
    const streamUrl = `${baseUrl}/series/${username}/${password}/${episode.id}.ts`;
    playStream(streamUrl, true);
    setShowSeriesModal(false);
  }, [selectedPlaylist, playStream]);

  const fetchEPG = async (streamId, channelName) => {
    if (!selectedPlaylist) return;
    const cached = epgCache[streamId];
    if (cached && Date.now() - cached.ts < 600000) { setEpgData(cached.data); return; }
    try {
      const res = await axios.get(`${API}/epg/xmltv`, {
        params: { device_key: deviceKey, playlist_id: selectedPlaylist.id, channel_id: streamId, channel_name: channelName || "" },
      });
      const listings = res.data?.epg_listings || [];
      setEpgData(listings);
      setEpgCache(prev => ({ ...prev, [streamId]: { data: listings, ts: Date.now() } }));
    } catch { setEpgData([]); }
  };

  const getCurrentProgram = (streamId) => {
    const cached = epgCache[streamId];
    if (!cached || !cached.data.length) return null;
    const now = new Date();
    return cached.data.find(item => { const s = new Date(item.start), e = new Date(item.end); return now >= s && now <= e; }) || cached.data[0];
  };

  useEffect(() => {
    if (contentType !== "live" || !selectedPlaylist || selectedPlaylist.playlist_type !== "xtream" || !deviceKey) return;
    if (!Array.isArray(channels) || channels.length === 0) return;
    const visible = channels.slice(0, 20);
    // FIX #6: track timer ids so we can cancel on dep change / unmount
    const timers = [];
    visible.forEach((ch, i) => {
      if (ch.stream_id && !epgCache[ch.stream_id]) {
        const t = setTimeout(() => {
          axios.get(`${API}/epg/xmltv`, { params: { device_key: deviceKey, playlist_id: selectedPlaylist.id, channel_id: ch.stream_id, channel_name: ch.name || "" } })
            .then(res => { const l = res.data?.epg_listings || []; setEpgCache(prev => ({ ...prev, [ch.stream_id]: { data: l, ts: Date.now() } })); })
            .catch(() => {});
        }, i * 300);
        timers.push(t);
      }
    });
    epgPrefetchTimersRef.current = timers;
    return () => {
      timers.forEach(t => clearTimeout(t));
      epgPrefetchTimersRef.current = [];
    };
  }, [channels, contentType, selectedPlaylist, deviceKey]);

  const toggleFavorite = (channel) => {
    const isFav = favorites.some((f) => f.stream_id === channel.stream_id);
    let newFavorites;
    if (isFav) {
      newFavorites = favorites.filter((f) => f.stream_id !== channel.stream_id);
      toast.success("Removed from favorites");
    } else {
      newFavorites = [...favorites, channel];
      toast.success("Added to favorites");
    }
    setFavorites(newFavorites);
    localStorage.setItem("nettv_favorites", JSON.stringify(newFavorites));
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !selectedChannel) return;
    const handleTimeUpdate = () => {
      if ((contentType === "movies" || contentType === "series") && video.duration > 0) {
        // FIX #9: throttle to once per 30s instead of every 5s
        const now = Date.now();
        if (now - (lastProgressSaveRef.current || 0) < 30000) return;
        lastProgressSaveRef.current = now;

        const key = selectedChannel.stream_id || selectedChannel.series_id;
        const progress = { time: video.currentTime, duration: video.duration, percent: Math.round((video.currentTime / video.duration) * 100), updatedAt: now };
        setWatchProgress(prev => {
          const updated = { ...prev, [key]: { ...progress, channel: selectedChannel } };
          // FIX #12: cap localStorage at 100 most-recent entries
          const entries = Object.entries(updated);
          if (entries.length > 100) {
            entries.sort((a, b) => (b[1]?.updatedAt || 0) - (a[1]?.updatedAt || 0));
            const trimmed = Object.fromEntries(entries.slice(0, 100));
            try { localStorage.setItem("nettv_progress", JSON.stringify(trimmed)); } catch(e) {}
            return trimmed;
          }
          try { localStorage.setItem("nettv_progress", JSON.stringify(updated)); } catch(e) {}
          return updated;
        });
      }
    };
    const interval = setInterval(() => { if (!video.paused) handleTimeUpdate(); }, 5000);
    // Also save on pause / visibility change for accuracy
    const onPause = () => { lastProgressSaveRef.current = 0; handleTimeUpdate(); };
    video.addEventListener('pause', onPause);
    return () => {
      clearInterval(interval);
      video.removeEventListener('pause', onPause);
    };
  }, [selectedChannel, contentType]);

  const restoreProgress = useCallback((channel) => {
    const key = channel.stream_id || channel.series_id;
    const prog = watchProgress[key];
    if (prog && prog.percent > 2 && prog.percent < 95) {
      // FIX #4: cancel any prior poll before starting a new one
      if (resumeIntervalRef.current) {
        clearInterval(resumeIntervalRef.current);
        resumeIntervalRef.current = null;
      }
      const targetKey = key;
      const check = setInterval(() => {
        // FIX #4: only seek if user hasn't switched to a different channel
        const stillSelected = selectedChannel && (selectedChannel.stream_id || selectedChannel.series_id) === targetKey;
        if (!stillSelected) {
          clearInterval(check);
          if (resumeIntervalRef.current === check) resumeIntervalRef.current = null;
          return;
        }
        if (videoRef.current && videoRef.current.duration > 0) {
          videoRef.current.currentTime = prog.time;
          toast.info("Resuming from " + Math.floor(prog.time / 60) + "m " + Math.floor(prog.time % 60) + "s");
          clearInterval(check);
          if (resumeIntervalRef.current === check) resumeIntervalRef.current = null;
        }
      }, 500);
      resumeIntervalRef.current = check;
      setTimeout(() => {
        clearInterval(check);
        if (resumeIntervalRef.current === check) resumeIntervalRef.current = null;
      }, 10000);
    }
  }, [watchProgress, selectedChannel]);

  // FIX #7: keep ref pointed at the latest restoreProgress so startPlayback's
  // setTimeout callback always sees current state
  useEffect(() => {
    restoreProgressRef.current = restoreProgress;
  }, [restoreProgress]);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) videoRef.current.pause();
    else videoRef.current.play();
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const toggleFullscreen = () => {
    if (!videoRef.current) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else videoRef.current.requestFullscreen();
  };

  const formatTime = (date) => date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
  const formatDate = (date) => date.toLocaleDateString("en-US", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });

  // FIX #17: stateful display limit instead of hardcoded slice(0, 300).
  // Resets to default when the visible list changes meaningfully.
  const [channelLimit, setChannelLimit] = useState(500);
  useEffect(() => {
    setChannelLimit(500);
  }, [selectedCategory, contentType, searchQuery, channels]);

  // FIX #5: memoize the filter so 28k-channel filtering doesn't run on every render
  const filteredChannels = useMemo(() => {
    let filtered = channels;
    if (selectedCategory === "favorites") filtered = favorites;
    else if (selectedCategory === "recent") filtered = recentlyViewed;
    else if (selectedCategory !== "all") {
      filtered = channels.filter(
        (ch) => String(ch.category_id) === String(selectedCategory) || ch.category_name?.toLowerCase().replace(/[^a-z0-9]/g, "_") === selectedCategory
      );
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((ch) => ch.name?.toLowerCase().includes(q));
    }
    return filtered;
  }, [channels, selectedCategory, favorites, recentlyViewed, searchQuery]);

  // FIX #13: memoize current-program lookup per stream_id (was walking epgCache per row, every render)
  const currentProgramByStreamId = useMemo(() => {
    const out = {};
    const now = new Date();
    for (const sid of Object.keys(epgCache)) {
      const cached = epgCache[sid];
      if (!cached || !cached.data || !cached.data.length) continue;
      let cur = null;
      for (const item of cached.data) {
        try {
          const s = new Date(item.start), e = new Date(item.end);
          if (now >= s && now <= e) { cur = item; break; }
        } catch (_) {}
      }
      out[sid] = cur || cached.data[0];
    }
    return out;
  }, [epgCache]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: brand.bg }}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: brand.accent }} />
          <p className="text-white/60 text-sm">{brand.name}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen nettv-bg text-white flex flex-col overflow-hidden" data-testid="player-page">
      {/* Header */}
      <header className="flex items-center justify-between px-2 sm:px-4 py-1.5 bg-black/60 border-b border-white/10 relative z-20 shrink-0">
        <div className="flex items-center gap-1.5 sm:gap-3 flex-1 min-w-0">
          <button onClick={() => navigate("/menu")} className="nettv-logo flex items-center shrink-0" data-testid="logo-home">
            <img src="/nettv-logo.png" alt="NetTV" className="h-6 sm:h-8" />
          </button>

          {/* Content Type Tabs */}
          <div className="flex items-center gap-0.5 bg-black/40 rounded-lg p-0.5 overflow-x-auto">
            {CONTENT_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => fetchContent(selectedPlaylist, tab.id)}
                className={`flex items-center gap-1 px-2 sm:px-3 py-1 rounded-md text-[10px] sm:text-xs font-medium transition-all whitespace-nowrap ${
                  contentType === tab.id
                    ? (brandId === "nettv" ? "bg-yellow-500 text-black" : "text-black font-semibold")
                    : "text-white/60 hover:text-white hover:bg-white/10"
                }`}
                style={contentType === tab.id && brandId !== "nettv"
                  ? { background: brand.accent, borderRadius: brandId === "pulseplayer" ? "9999px" : "6px" }
                  : undefined}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.id === "live" ? "LIVE" : tab.id === "movies" ? "VOD" : tab.id === "series" ? "SERIES" : "RADIO"}</span>
              </button>
            ))}
          </div>

          {/* Search - hidden on very small screens */}
          <div className="relative hidden sm:block ml-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 pr-3 py-1 bg-black/30 border border-white/10 rounded text-xs text-white placeholder:text-white/30 w-32 lg:w-40 focus:border-cyan-400 focus:outline-none"
              data-testid="search-input"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 text-xs shrink-0">
          {playlists.length > 1 && (
            <select
              value={selectedPlaylist?.id || ""}
              onChange={(e) => {
                const pl = playlists.find((p) => p.id === e.target.value);
                setSelectedPlaylist(pl);
                localStorage.setItem("selectedPlaylistId", pl?.id);
                fetchContent(pl, contentType);
              }}
              className="px-2 py-1 bg-black/50 border border-white/10 rounded text-white text-xs max-w-[100px]"
              data-testid="playlist-selector"
            >
              {playlists.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          )}
          <button onClick={() => navigate("/settings")} className="flex items-center gap-1 text-white/60 hover:text-white">
            <Settings className="w-3.5 h-3.5" />
          </button>
          <span className="text-white/50 hidden sm:inline">{formatTime(currentTime)}</span>
        </div>
      </header>

      {/* Mobile Panel Switcher */}
      <div className="flex sm:hidden items-center bg-black/40 border-b border-white/10 shrink-0">
        {/* Mobile search */}
        <div className="relative flex-1 mx-2 my-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-white/40" />
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-7 pr-2 py-1 bg-black/30 border border-white/10 rounded text-xs text-white placeholder:text-white/30 focus:border-cyan-400 focus:outline-none"
            data-testid="search-input-mobile"
          />
        </div>
        <button
          onClick={() => setMobilePanel("categories")}
          className={`px-3 py-2 text-[10px] font-medium ${mobilePanel === "categories" ? "text-yellow-400 border-b-2 border-yellow-400" : "text-white/50"}`}
        >
          GROUPS
        </button>
        <button
          onClick={() => setMobilePanel("channels")}
          className={`px-3 py-2 text-[10px] font-medium ${mobilePanel === "channels" ? "text-yellow-400 border-b-2 border-yellow-400" : "text-white/50"}`}
        >
          LIST
        </button>
        <button
          onClick={() => setMobilePanel("player")}
          className={`px-3 py-2 text-[10px] font-medium ${mobilePanel === "player" ? "text-yellow-400 border-b-2 border-yellow-400" : "text-white/50"}`}
        >
          PLAYER
        </button>
      </div>

      {/* Main Content - 3 Panel Layout (desktop) / Single Panel (mobile) */}
      <main className="flex-1 flex relative z-10 min-h-0">
        {/* Left Panel - Categories */}
        <aside className={`w-52 nettv-panel flex-col shrink-0 ${mobilePanel === "categories" ? "flex sm:flex" : "hidden sm:flex"}`} data-testid="categories-panel">
          <div className="nettv-panel-header flex items-center justify-between text-xs">
            <span>GROUPS</span>
            <span className="text-cyan-400">{categories.length + 2}</span>
          </div>
          <div className="flex-1 overflow-y-auto">
            <button
              onClick={() => { setSelectedCategory("recent"); setMobilePanel("channels"); }}
              className={`nettv-category-item w-full text-xs ${selectedCategory === "recent" ? "active" : ""}`}
              data-testid="cat-recent"
            >
              <Clock className="w-3.5 h-3.5" />
              <span>Recently Viewed</span>
              <span className="ml-auto text-[10px] opacity-60">{recentlyViewed.length}</span>
            </button>
            <button
              onClick={() => { setSelectedCategory("favorites"); setMobilePanel("channels"); }}
              className={`nettv-category-item w-full text-xs ${selectedCategory === "favorites" ? "active" : ""}`}
              data-testid="cat-favorites"
            >
              <Star className="w-3.5 h-3.5" />
              <span>Favourites</span>
              <span className="ml-auto text-[10px] opacity-60">{favorites.length}</span>
            </button>
            <button
              onClick={() => { setSelectedCategory("all"); setMobilePanel("channels"); }}
              className={`nettv-category-item w-full text-xs ${selectedCategory === "all" ? "active" : ""}`}
              data-testid="cat-all"
            >
              <List className="w-3.5 h-3.5" />
              <span>All {contentType === "movies" ? "Movies" : contentType === "series" ? "Series" : contentType === "radio" ? "Stations" : "Channels"}</span>
              <span className="ml-auto text-[10px] opacity-60">{channels.length}</span>
            </button>
            {/* FIX #16: removed silent slice(0, 200) — was hiding 354 of 554 categories */}
            {categories.map((cat) => (
              <button
                key={cat.category_id}
                onClick={() => { setSelectedCategory(cat.category_id); setMobilePanel("channels"); }}
                className={`nettv-category-item w-full text-xs ${selectedCategory === cat.category_id ? "active" : ""}`}
              >
                <span className="truncate">{cat.category_name}</span>
              </button>
            ))}
          </div>
        </aside>

        {/* Middle Panel - Channels/Content List */}
        <div className={`w-full sm:w-72 nettv-panel flex-col shrink-0 ${mobilePanel === "channels" ? "flex sm:flex" : "hidden sm:flex"}`} data-testid="channels-panel">
          <div className="nettv-panel-header flex items-center justify-between text-xs">
            <span>{contentType === "movies" ? "MOVIES" : contentType === "series" ? "SERIES" : contentType === "radio" ? "STATIONS" : "CHANNELS"}</span>
            <span className="text-cyan-400">{filteredChannels.length}</span>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loadingChannels ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
              </div>
            ) : filteredChannels.length === 0 ? (
              <div className="text-center py-8 text-white/40">
                <Tv className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-xs">No content found</p>
              </div>
            ) : (
              filteredChannels.slice(0, channelLimit).map((channel, idx) => {
                // FIX #15: robust active-channel comparison (handles stream_id===0 / series_id-only items)
                const isActive = !!selectedChannel && (
                  (channel.stream_id != null && selectedChannel.stream_id === channel.stream_id) ||
                  (channel.series_id != null && selectedChannel.series_id === channel.series_id)
                );
                return (
                <div
                  key={channel.stream_id || channel.series_id || idx}
                  onClick={() => {
                    if (contentType === "series") handleSeriesClick(channel);
                    else { playChannel(channel); setMobilePanel("player"); }
                  }}
                  className={`nettv-channel-item ${isActive ? "active" : ""}`}
                  data-testid={`channel-${idx}`}
                >
                  <img
                    src={channel.stream_icon || channel.cover || `${API}/logo-lookup?name=${encodeURIComponent(channel.name || "")}`}
                    alt=""
                    className={`${contentType === "movies" || contentType === "series" ? "w-10 h-14" : "w-10 h-8"} object-contain bg-black/30 rounded`}
                    onError={(e) => {
                      if (!e.target.dataset.fb) {
                        e.target.dataset.fb = "1";
                        e.target.src = `${API}/logo-lookup?name=${encodeURIComponent(channel.name || "")}`;
                      } else {
                        e.target.style.visibility = "hidden";
                      }
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs truncate">{channel.name}</p>
                    {contentType === "movies" && channel.rating && (
                      <p className="text-[10px] text-yellow-400">{channel.rating}/10</p>
                    )}
                    {contentType === "series" && (
                      <p className="text-[10px] text-cyan-400">Series</p>
                    )}
                    {contentType === "movies" && watchProgress[channel.stream_id]?.percent > 2 && (
                      <div className="mt-0.5 h-0.5 bg-white/10 rounded-full overflow-hidden w-full">
                        <div className="h-full bg-cyan-400 rounded-full" style={{width: watchProgress[channel.stream_id].percent + "%"}} />
                      </div>
                    )}
                    {contentType === "live" && (() => {
                      // FIX #13: O(1) memoized lookup instead of per-row epgCache walk
                      const prog = currentProgramByStreamId[channel.stream_id];
                      return prog ? <p className="text-[10px] text-white/40 truncate">{prog.title}</p> : <p className="text-[10px] text-green-400">LIVE</p>;
                    })()}
                    {contentType === "radio" && (
                      <p className="text-[10px] text-purple-400">RADIO</p>
                    )}
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleFavorite(channel); }}
                    className={`p-0.5 rounded ${favorites.some((f) => f.stream_id === channel.stream_id) ? "text-yellow-400" : "text-white/20 hover:text-white/50"}`}
                  >
                    <Star className="w-3.5 h-3.5" fill={favorites.some((f) => f.stream_id === channel.stream_id) ? "currentColor" : "none"} />
                  </button>
                </div>
                );
              })
            )}
            {/* FIX #17: "Show more" button instead of silent 300-channel cap */}
            {filteredChannels.length > channelLimit && (
              <button
                onClick={() => setChannelLimit(l => l + 500)}
                className="w-full py-2 text-xs text-cyan-400 hover:text-cyan-300 hover:bg-white/5 transition-colors border-t border-white/5"
                data-testid="show-more-channels"
              >
                Show more ({filteredChannels.length - channelLimit} remaining)
              </button>
            )}
          </div>
        </div>

        {/* Right Panel - Video & EPG */}
        <div className={`flex-1 flex-col p-2 sm:p-3 gap-2 sm:gap-3 min-w-0 ${mobilePanel === "player" ? "flex sm:flex" : "hidden sm:flex"}`}>
          {/* Video Player */}
          <div className="relative bg-black rounded-lg overflow-hidden flex-1 min-h-0" data-testid="video-player">
            {/* FIX #1: object-contain (was object-fill — was distorting aspect ratio)
                FIX #2: drop native `controls` (we already render a custom control bar below)
                FIX #3: start muted so autoPlay isn't blocked; user unmutes via custom button
                FIX #8: drop `crossOrigin="anonymous"` — was making any non-CORS upstream error fatal */}
            <video
              ref={videoRef}
              className="w-full h-full object-contain bg-black"
              playsInline
              autoPlay
              muted={isMuted}
            />
            {!selectedChannel && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/90">
                {contentType === "radio" ? (
                  <Radio className="w-12 h-12 sm:w-16 sm:h-16 text-purple-400/30 mb-4" />
                ) : (
                  <Tv className="w-12 h-12 sm:w-16 sm:h-16 text-white/15 mb-4" />
                )}
                <p className="text-white/40 text-xs sm:text-sm">
                  Select {contentType === "movies" ? "a movie" : contentType === "series" ? "a series" : contentType === "radio" ? "a station" : "a channel"} to start
                </p>
              </div>
            )}
            {streamError && (
              <div className="absolute bottom-4 left-4 right-4 bg-red-900/85 text-red-100 px-3 py-2.5 rounded-lg text-xs flex flex-col gap-2 shadow-lg">
                <div className="flex items-center gap-2">
                  <Info className="w-4 h-4 shrink-0" />
                  <span className="flex-1">{streamError}</span>
                </div>
                {/* FIX #18: external-player fallback buttons (Android intents).
                    Only shown when we know the upstream URL (unplayableStream is set). */}
                {unplayableStream && (() => {
                  const u = unplayableStream;
                  const direct = u.proxyUrl || u.url;
                  const intentUrl = (pkg) => {
                    const noScheme = direct.replace(/^https?:\/\//, "");
                    const scheme = direct.startsWith("https") ? "https" : "http";
                    const title = encodeURIComponent(u.name || "stream");
                    return `intent://${noScheme}#Intent;type=video/*;scheme=${scheme};package=${pkg};S.title=${title};end`;
                  };
                  const openIntent = (pkg) => {
                    try { window.location.href = intentUrl(pkg); }
                    catch (e) { window.open(direct, "_blank"); }
                  };
                  return (
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => openIntent("org.videolan.vlc")}
                        className="px-2.5 py-1 rounded bg-orange-500/30 hover:bg-orange-500/50 text-orange-100 text-[11px] font-medium transition-all"
                        data-testid="open-in-vlc"
                      >
                        Open in VLC
                      </button>
                      <button
                        onClick={() => openIntent("com.mxtech.videoplayer.ad")}
                        className="px-2.5 py-1 rounded bg-blue-500/30 hover:bg-blue-500/50 text-blue-100 text-[11px] font-medium transition-all"
                        data-testid="open-in-mxplayer"
                      >
                        Open in MX Player
                      </button>
                      <a
                        href={direct}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="px-2.5 py-1 rounded bg-white/15 hover:bg-white/25 text-white text-[11px] font-medium transition-all"
                        data-testid="open-direct-link"
                      >
                        Direct link
                      </a>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Channel Info / EPG — brand-aware HUD */}
          <div
            className="shrink-0"
            data-testid="epg-panel"
            style={{
              background: brandId === "arcplayer" ? "rgba(2,12,22,0.95)"
                : brandId === "pulseplayer"       ? "rgba(5,3,15,0.95)"
                : undefined,
              borderTop: `1px solid ${brand.accent}25`,
              backdropFilter: "blur(10px)",
              padding: "8px 10px 8px 10px",
            }}
          >
            {/* ArcPlayer: 1px sharp accent rule */}
            {brandId === "arcplayer" && (
              <div style={{ height:"1px", background:"#00BFFF", opacity:0.45, marginBottom:"8px" }} />
            )}
            {/* PulsePlayer: 2px gradient rule */}
            {brandId === "pulseplayer" && (
              <div style={{ height:"2px", background:"linear-gradient(90deg,#00E5FF,#A855F7)", marginBottom:"8px", borderRadius:"2px" }} />
            )}
            <div className="nettv-epg-header flex items-center gap-3">
              {selectedChannel ? (
                <>
                  <img
                    src={selectedChannel.stream_icon || selectedChannel.cover || `${API}/logo-lookup?name=${encodeURIComponent(selectedChannel.name || "")}`}
                    alt=""
                    className="w-10 h-8 object-contain"
                    style={{ borderRadius: brandId === "pulseplayer" ? "6px" : "3px" }}
                    onError={(e) => {
                      if (!e.target.dataset.fb) {
                        e.target.dataset.fb = "1";
                        e.target.src = `${API}/logo-lookup?name=${encodeURIComponent(selectedChannel.name || "")}`;
                      } else {
                        e.target.style.visibility = "hidden";
                      }
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{selectedChannel.name}</p>
                    <p className="text-xs text-white/50">{selectedChannel.category_name || contentType.toUpperCase()}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={togglePlay}
                      data-testid="play-pause-btn"
                      className="p-1.5"
                      style={brandId === "arcplayer"
                        ? { borderRadius:"4px", background:"rgba(0,191,255,0.15)", border:"1px solid rgba(0,191,255,0.4)", color:"#00BFFF" }
                        : brandId === "pulseplayer"
                        ? { borderRadius:"9999px", background:"linear-gradient(135deg,rgba(0,229,255,0.2),rgba(168,85,247,0.2))", border:"1px solid rgba(0,229,255,0.4)", color:"#00E5FF", boxShadow:"0 0 8px rgba(0,229,255,0.25)" }
                        : { borderRadius:"6px", background:"rgba(255,255,255,0.1)" }}
                    >
                      {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={toggleMute}
                      data-testid="mute-btn"
                      className="p-1.5"
                      style={brandId === "arcplayer"
                        ? { borderRadius:"4px", background:"rgba(0,191,255,0.07)", border:"1px solid rgba(0,191,255,0.2)", color:"rgba(255,255,255,0.75)" }
                        : brandId === "pulseplayer"
                        ? { borderRadius:"9999px", background:"rgba(168,85,247,0.12)", border:"1px solid rgba(168,85,247,0.3)", color:"rgba(255,255,255,0.75)" }
                        : { borderRadius:"6px", background:"rgba(255,255,255,0.1)" }}
                    >
                      {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={toggleFullscreen}
                      data-testid="fullscreen-btn"
                      className="p-1.5"
                      style={brandId === "arcplayer"
                        ? { borderRadius:"4px", background:"rgba(0,191,255,0.07)", border:"1px solid rgba(0,191,255,0.2)", color:"rgba(255,255,255,0.75)" }
                        : brandId === "pulseplayer"
                        ? { borderRadius:"9999px", background:"rgba(168,85,247,0.12)", border:"1px solid rgba(168,85,247,0.3)", color:"rgba(255,255,255,0.75)" }
                        : { borderRadius:"6px", background:"rgba(255,255,255,0.1)" }}
                    >
                      <Maximize className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => toggleFavorite(selectedChannel)}
                      className="p-1.5"
                      style={favorites.some((f) => f.stream_id === selectedChannel.stream_id)
                        ? { borderRadius: brandId === "pulseplayer" ? "9999px" : "4px", color:"#facc15", background:"rgba(250,204,21,0.12)" }
                        : brandId === "arcplayer"
                        ? { borderRadius:"4px", color:"rgba(255,255,255,0.35)", background:"rgba(0,191,255,0.05)", border:"1px solid rgba(0,191,255,0.15)" }
                        : brandId === "pulseplayer"
                        ? { borderRadius:"9999px", color:"rgba(255,255,255,0.35)", background:"rgba(168,85,247,0.08)", border:"1px solid rgba(168,85,247,0.2)" }
                        : { borderRadius:"6px", color:"rgba(255,255,255,0.4)", background:"rgba(255,255,255,0.1)" }}
                    >
                      <Star className="w-4 h-4" fill={favorites.some((f) => f.stream_id === selectedChannel.stream_id) ? "currentColor" : "none"} />
                    </button>
                  </div>
                </>
              ) : (
                <p className="text-white/40 text-xs">No channel selected</p>
              )}
            </div>

            {/* EPG Schedule */}
            <div className="p-3 max-h-28 overflow-y-auto">
              {epgData.length > 0 ? (
                epgData.slice(0, 5).map((item, i) => {
                  // FIX #14: defensively render EPG times — handle ISO, unix, or pre-formatted strings
                  const fmtTime = (raw) => {
                    if (!raw) return "";
                    if (typeof raw === "number") {
                      try { return new Date(raw * (raw < 1e12 ? 1000 : 1)).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false }); }
                      catch (_) { return String(raw); }
                    }
                    const s = String(raw);
                    if (/^\d{2}:\d{2}/.test(s)) return s.slice(0, 5);
                    const d = new Date(s);
                    if (!isNaN(d.getTime())) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
                    return s.slice(0, 5);
                  };
                  return (
                    <div key={i} className={`nettv-epg-item ${i === 0 ? "current" : ""}`}>
                      <span className={`w-28 text-xs shrink-0 ${i === 0 ? "text-cyan-400" : "text-white/40"}`}>
                        {fmtTime(item.start)} - {fmtTime(item.end)}
                      </span>
                      <span className={`flex-1 text-xs truncate ${i === 0 ? "" : "text-white/60"}`}>
                        {item.title || "No Title"}
                      </span>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-2 text-white/30 text-xs">
                  {selectedChannel ? "No EPG data available" : "Select a channel to view schedule"}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="px-2 sm:px-4 py-1.5 bg-black/60 border-t border-white/10 flex items-center justify-between text-[10px] sm:text-xs relative z-10 shrink-0">
        <div className="flex items-center gap-2 sm:gap-4 text-white/40">
          <span>Device: <span className="text-cyan-400 font-mono">{deviceKey}</span></span>
          <span className="hidden sm:inline">
            Expiry: <span className="text-cyan-400">{deviceStatus?.is_paid ? "LIFETIME" : deviceStatus?.trial_end ? new Date(deviceStatus.trial_end).toLocaleDateString() : "N/A"}</span>
          </span>
        </div>
        <button onClick={() => navigate("/menu")} className="text-white/50 hover:text-white flex items-center gap-1" data-testid="back-menu-btn">
          <ArrowLeft className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> Menu
        </button>
      </footer>

      {/* Movie Info Modal - Netflix Style */}
      {showMovieModal && movieInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 info-modal-backdrop" data-testid="movie-info-modal">
          <div className="info-modal-content max-w-3xl w-full max-h-[90vh] sm:max-h-[85vh] flex flex-col rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>

            {/* Backdrop / Hero */}
            <div className="relative h-40 sm:h-56 shrink-0 overflow-hidden">
              {(movieInfo.stream_icon || movieInfo.cover_big || movieInfo.cover || movieInfo.movie_image) ? (
                <img
                  src={movieInfo.cover_big || movieInfo.movie_image || movieInfo.stream_icon || movieInfo.cover}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-indigo-900 to-slate-900" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e1a] via-[#0a0e1a]/60 to-transparent" />

              {/* Close button */}
              <button
                onClick={() => setShowMovieModal(false)}
                className="absolute top-3 right-3 p-2 rounded-full bg-black/60 text-white/70 hover:text-white hover:bg-black/80 transition-all"
                data-testid="close-movie-modal"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Title overlay */}
              <div className="absolute bottom-4 left-5 right-5">
                <h2 className="text-2xl font-bold text-white leading-tight drop-shadow-lg">{movieInfo.name || movieInfo.title || "Movie"}</h2>
                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  {movieInfo.rating && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-400 text-xs font-semibold">
                      <Star className="w-3 h-3" fill="currentColor" /> {movieInfo.rating}
                    </span>
                  )}
                  {(movieInfo.releasedate || movieInfo.year) && (
                    <span className="inline-flex items-center gap-1 text-white/60 text-xs">
                      <Calendar className="w-3 h-3" /> {movieInfo.releasedate || movieInfo.year}
                    </span>
                  )}
                  {(movieInfo.duration || movieInfo.duration_secs) && (
                    <span className="inline-flex items-center gap-1 text-white/60 text-xs">
                      <Timer className="w-3 h-3" /> {movieInfo.duration || `${Math.round((movieInfo.duration_secs || 0) / 60)} min`}
                    </span>
                  )}
                  {movieInfo.genre && (
                    <span className="inline-flex items-center gap-1 text-white/60 text-xs">
                      <Tag className="w-3 h-3" /> {movieInfo.genre}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {/* Action Buttons */}
              <div className="flex items-center gap-3">
                <button
                  onClick={playMovieFromModal}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold text-sm hover:from-cyan-400 hover:to-blue-500 transition-all shadow-lg shadow-cyan-500/25"
                  data-testid="play-movie-btn"
                >
                  <Play className="w-5 h-5" fill="currentColor" /> Play Now
                </button>
                <button
                  onClick={() => toggleFavorite(movieInfo)}
                  className={`p-2.5 rounded-lg border transition-all ${
                    favorites.some((f) => f.stream_id === movieInfo.stream_id)
                      ? "border-yellow-500/50 bg-yellow-500/10 text-yellow-400"
                      : "border-white/10 bg-white/5 text-white/50 hover:text-white hover:border-white/30"
                  }`}
                  data-testid="fav-movie-btn"
                >
                  <Star className="w-5 h-5" fill={favorites.some((f) => f.stream_id === movieInfo.stream_id) ? "currentColor" : "none"} />
                </button>
              </div>

              {/* Plot / Description */}
              {(movieInfo.plot || movieInfo.description) && (
                <div>
                  <h4 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-1.5">Synopsis</h4>
                  <p className="text-sm text-white/70 leading-relaxed">{movieInfo.plot || movieInfo.description}</p>
                </div>
              )}

              {/* Cast & Director */}
              {(movieInfo.cast || movieInfo.director) && (
                <div className="grid grid-cols-2 gap-4">
                  {movieInfo.director && (
                    <div>
                      <h4 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-1">Director</h4>
                      <p className="text-sm text-white/70">{movieInfo.director}</p>
                    </div>
                  )}
                  {movieInfo.cast && (
                    <div>
                      <h4 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-1">Cast</h4>
                      <p className="text-sm text-white/70 line-clamp-3">{movieInfo.cast}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Additional Info */}
              {(movieInfo.country || movieInfo.category_name) && (
                <div className="flex items-center gap-4 text-xs text-white/50">
                  {movieInfo.country && <span>Country: {movieInfo.country}</span>}
                  {movieInfo.category_name && <span>Category: {movieInfo.category_name}</span>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Series Info Modal - Netflix Style */}
      {showSeriesModal && seriesInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 info-modal-backdrop" data-testid="series-info-modal">
          <div className="info-modal-content max-w-3xl w-full max-h-[90vh] sm:max-h-[85vh] flex flex-col rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>

            {/* Backdrop / Hero */}
            <div className="relative h-36 sm:h-48 shrink-0 overflow-hidden">
              {(seriesInfo.info?.cover || seriesInfo._channel?.cover || seriesInfo._channel?.stream_icon) ? (
                <img
                  src={seriesInfo.info?.cover || seriesInfo._channel?.cover || seriesInfo._channel?.stream_icon}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-purple-900 to-slate-900" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e1a] via-[#0a0e1a]/60 to-transparent" />

              <button
                onClick={() => setShowSeriesModal(false)}
                className="absolute top-3 right-3 p-2 rounded-full bg-black/60 text-white/70 hover:text-white hover:bg-black/80 transition-all"
                data-testid="close-series-modal"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="absolute bottom-4 left-5 right-5">
                <h2 className="text-2xl font-bold text-white leading-tight drop-shadow-lg">
                  {seriesInfo.info?.name || seriesInfo._channel?.name || "Series"}
                </h2>
                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  {seriesInfo.info?.rating && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-400 text-xs font-semibold">
                      <Star className="w-3 h-3" fill="currentColor" /> {seriesInfo.info.rating}
                    </span>
                  )}
                  {seriesInfo.info?.releaseDate && (
                    <span className="inline-flex items-center gap-1 text-white/60 text-xs">
                      <Calendar className="w-3 h-3" /> {seriesInfo.info.releaseDate}
                    </span>
                  )}
                  {seriesInfo.info?.genre && (
                    <span className="inline-flex items-center gap-1 text-white/60 text-xs">
                      <Tag className="w-3 h-3" /> {seriesInfo.info.genre}
                    </span>
                  )}
                  {seriesInfo.info?.cast && (
                    <span className="inline-flex items-center gap-1 text-white/60 text-xs">
                      <Users className="w-3 h-3" /> {typeof seriesInfo.info.cast === 'string' ? seriesInfo.info.cast.substring(0, 50) : ''}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Plot */}
            {(seriesInfo.info?.plot || seriesInfo.info?.description) && (
              <div className="px-5 pt-4 pb-2">
                <p className="text-sm text-white/60 leading-relaxed line-clamp-3">
                  {seriesInfo.info.plot || seriesInfo.info.description}
                </p>
              </div>
            )}

            {/* Season Tabs */}
            {seriesInfo.episodes && Object.keys(seriesInfo.episodes).length > 0 && (
              <div className="flex gap-1.5 px-5 pt-3 pb-2 overflow-x-auto shrink-0">
                {Object.keys(seriesInfo.episodes).map((season) => (
                  <button
                    key={season}
                    onClick={() => setSelectedSeason(season)}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                      selectedSeason === season
                        ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/20"
                        : "bg-white/5 text-white/50 hover:text-white hover:bg-white/10"
                    }`}
                    data-testid={`season-${season}`}
                  >
                    Season {season}
                  </button>
                ))}
              </div>
            )}

            {/* Episodes */}
            <div className="flex-1 overflow-y-auto px-5 pb-5 pt-2 space-y-1.5">
              {selectedSeason && seriesInfo.episodes?.[selectedSeason]?.map((ep, i) => (
                <button
                  key={ep.id || i}
                  onClick={() => playEpisode(ep)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] hover:bg-cyan-500/10 border border-transparent hover:border-cyan-500/20 transition-all text-left group"
                  data-testid={`episode-${i}`}
                >
                  {ep.info?.movie_image ? (
                    <img src={ep.info.movie_image} alt="" className="w-20 h-12 rounded-lg object-cover shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div className="w-20 h-12 rounded-lg bg-white/5 flex items-center justify-center shrink-0 group-hover:bg-cyan-500/20 transition-all">
                      <Play className="w-5 h-5 text-white/30 group-hover:text-cyan-400 transition-all" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate group-hover:text-cyan-300 transition-colors">
                      E{ep.episode_num || i + 1}: {ep.title || `Episode ${ep.episode_num || i + 1}`}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {ep.info?.duration && <span className="text-[10px] text-white/40">{ep.info.duration}</span>}
                      {ep.info?.plot && <span className="text-[10px] text-white/30 truncate">{ep.info.plot}</span>}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-white/20 group-hover:text-cyan-400 transition-all shrink-0" />
                </button>
              ))}
              {selectedSeason && (!seriesInfo.episodes?.[selectedSeason] || seriesInfo.episodes[selectedSeason].length === 0) && (
                <p className="text-center text-white/40 text-sm py-6">No episodes available for this season</p>
              )}
              {!seriesInfo.episodes || Object.keys(seriesInfo.episodes).length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-white/40 text-sm mb-4">No episode data available</p>
                  {seriesInfo._channel?.stream_url && (
                    <button
                      onClick={() => {
                        setShowSeriesModal(false);
                        startPlayback(seriesInfo._channel);
                      }}
                      className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold text-sm hover:from-cyan-400 hover:to-blue-500 transition-all shadow-lg shadow-cyan-500/25"
                      data-testid="play-series-direct-btn"
                    >
                      <Play className="w-4 h-4" fill="currentColor" /> Play Stream
                    </button>
                  )}
                  {!seriesInfo._channel?.stream_url && (
                    <p className="text-white/30 text-xs">This series requires an Xtream Codes playlist for episode browsing</p>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Loading info overlay */}
      {loadingInfo && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
            <p className="text-white/60 text-sm">Loading info...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerPage;
